import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { RouterLink } from '@angular/router';

/**
 * refresh-parent-portal-ui 0.1: one list row (20 px radius, 72 px min height).
 * Slots: [leading] (illustration/avatar), title/subtitle inputs, [trailing]
 * (pill/menu/link). The row is an anchor; it only gets an href (and the
 * clickable look) when `link` is set — otherwise it is an inert row.
 *
 * 2.1 fix: the row used to be an <a> in one @if branch and a <div> in the
 * other, with the same ng-content selects duplicated across both. Angular
 * binds projected content to the FIRST declared occurrence, so everything
 * projected into a no-link row was silently dropped. One element, one
 * ng-content per selector.
 *
 * 2.2: the optional `queryParams` input rides the link (the wizard's rows
 * open `?form=<formKey>` screens on the same route).
 */
@Component({
  selector: 'ui-list-row',
  standalone: true,
  imports: [RouterLink],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <a class="row" [class.clickable]="!!link()" [routerLink]="link()" [queryParams]="queryParams()">
      <ng-content select="[leading]"></ng-content>
      <span class="main">
        <span class="title">{{ title() }}</span>
        @if (subtitle()) {
          <span class="sub">{{ subtitle() }}</span>
        }
      </span>
      <span class="trail"><ng-content select="[trailing]"></ng-content></span>
    </a>
  `,
  styles: [
    `
      :host {
        display: block;
      }
      .row {
        display: flex;
        align-items: center;
        gap: 10px;
        background: var(--gp-surface);
        border: 1.5px solid var(--gp-border);
        border-radius: var(--gp-radius-row);
        padding: 8px 10px;
        min-height: 60px;
        text-decoration: none;
        color: var(--gp-ink);
      }
      .clickable {
        cursor: pointer;
      }
      .main {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 3px;
      }
      .title {
        font-weight: 800;
        font-size: 15px;
        line-height: 19px;
      }
      .sub {
        font-size: 13px;
        color: var(--gp-ink-muted);
      }
      .trail {
        flex: 0 0 auto;
        display: flex;
        align-items: center;
        gap: 6px;
      }
    `,
  ],
})
export class UiListRowComponent {
  readonly title = input.required<string>();
  readonly subtitle = input('');
  readonly link = input<unknown[] | string | undefined>(undefined);
  /** 2.2: optional query params for the link (the wizard's ?form= views). */
  readonly queryParams = input<{ [key: string]: unknown } | undefined>(undefined);
}